<template>
  <!-- .profile -->
  <div class="container edit-user-template company-edit">
    <div class="container edit-user-template">
      <div class="loading" v-if="dataLoading">
        <div class="loading-text">loading ...</div>
      </div>
      <div class="page-header color-1-100">
        <h1>
          <template v-if="canEditDetails">
            Edit organisation details:
          </template>
          <template v-if="getUserRole === 'gm'">
            {{ this.company.details.name }}
          </template>
          <template v-else>
            My company profile
          </template>
        </h1>
        <template v-if="getUserRole === 'gm'">
          <div class="profile-actions float-none">
            <button v-on:click.prevent="archiveProfile()" class="archive-profile">
              <template v-if="company.details.enabled.value">
                Archive
              </template>
              <template v-else>
                Unarchive
              </template>
            </button>
            <button v-on:click.prevent="deleteProfile()" class="delete-profile background-1-20 color-2-100">Delete
            </button>
            <button v-on:click.prevent="resetTaskHistory()" class="delete-profile background-1-20 color-2-100" v-if="company.details.isDemo">
              Reset task history
            </button>
          </div>
        </template>
      </div>
      <template v-if="getUserRole !== 'gm'">
        <div class="admin-breadcrumbs">
          <router-link class="dashboard background-1-20 color-2-100" to="/">Go to Dashboard</router-link>
        </div>
      </template>
      <template v-else>
        <div class="admin-gm-breadcrumbs" tabindex="-1">
          <button
            class="dashboard"
            tabindex="0"
            :class="[{ 'active' : this.tabActive === 'profile' }]"
            @click.prevent="changeTabActive('profile')"
          >Profile
          </button>
          <button class="dashboard"
                  tabindex="0"
                  :class="[{ 'active' : this.tabActive === 'billing' }]"
                  @click.prevent="changeTabActive('billing')">Billing</button>
          <router-link :to="{name: 'company-tasks', params: { companyId: company.details.id }}" v-if="company.details.id">Tasks</router-link>
          <button
            class="progress"
            tabindex="0"
            :class="[{ 'active' : this.tabActive === 'progress' }]"
            @click.prevent="changeTabActive('progress')"
          >Progress
          </button>
          <template v-if="company.details.industry && company.details.industry.landing">
            <router-link class="landing"
                         :to="{ name: 'company-landing', params: { id: company.details.industry.landing.slug } }">
              Landing
            </router-link>
            <router-link class="landing"
                         :to="{ name: 'company-registration-landing', params: {
                         industry: company.details.industry.landing.slug,
                         company: company.details.url.toLowerCase() } }"
                         v-if="company.details.allowRegistration && company.details.allowRegistration.value && company.details.url">
              Registration landing
            </router-link>
          </template>
        </div>
      </template>
      <div class="flex-block">
        <div class="content-right-section anchors" v-if="this.tabActive === 'profile' || this.tabActive === 'progress'">
          <div class="sticky-anchors background-1-40-before" tabindex="-1">
            <template v-if="this.tabActive === 'profile'">
              <div class="anchor-item color-1-20 details"
                   :class="[{ 'active  color-1-100': activeSection === 'companyDetails' }]">
                <button @click.prevent="goToPageSection('companyDetails')">
                  <template v-if="canEditDetails === false">Organisation details</template>
                  <template v-else>Edit organisation details</template>
                </button>
              </div>
              <div class="anchor-item color-1-20 owner"
                   :class="[{ 'active  color-1-100': activeSection === 'companyOwner' }]">
                <button @click.prevent="goToPageSection('companyOwner')">
                  <template v-if="canEditOwner === false">Account owner details</template>
                  <template v-else>Edit account owner details</template>
                </button>
              </div>
              <div class="anchor-item color-1-20 details"
                   v-if="getUserRole === 'ca' || getUserRole === 'gm'"
                   :class="[{ 'active  color-1-100': activeSection === 'tasksSettings' }]">
                <button @click.prevent="goToPageSection('tasksSettings')">
                  <template v-if="canEditDetails === false">Tasks settings</template>
                  <template v-else>Edit tasks settings</template>
                </button>
              </div>
              <div class="anchor-item color-1-20 email-endings"
                   :class="[{ 'active  color-1-100': activeSection === 'companyRegistration' }]">
                <button @click.prevent="goToPageSection('companyRegistration')">
                  <template v-if="canEditCompanyRegistration === false">User registration settings</template>
                  <template v-else>Edit user registration settings</template>
                </button>
              </div>
            </template>
            <template v-else-if="this.tabActive === 'progress'">
              <div class="anchor-item color-1-20 progress"
                   :class="[{ 'active  color-1-100': activeSection === 'companyChart' }]">
                <button @click.prevent="goToPageSection('companyChart')">Company chart</button>
              </div>
              <div class="anchor-item color-1-20 progress"
                   :class="[{ 'active  color-1-100': activeSection === 'companyProgress' }]">
                <button @click.prevent="goToPageSection('companyProgress')">
                  <template v-if="canEditProgressChapters === false">Company progress</template>
                  <template v-else>Edit company progress</template>
                </button>
              </div>
            </template>
          </div>
        </div>
        <div class="content-left-section">
          <template v-if="this.tabActive === 'profile'">
            <div class="content-section content-section__bordered border-1-20 profile-section no-margin-top"
                 tabindex="-1"
                 ref="companyDetails">
              <h3 class="content-section-title color-1-100 background-1-100-before">
                <template v-if="canEditDetails === false">Organisation details</template>
                <template v-else>Edit organisation details</template>
              </h3>
              <button v-on:click.prevent="openEditDetails()" class="edit-details border-1-40 color-2-100"
                      v-if="canEditDetails === false">Edit
              </button>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="new-company-name" class="form-label color-2-63">Company name</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :class="[{ 'has-error': errors.has('Company name'), 'is-locked': !canEditDetails }]"
                         :disabled="canEditDetails === false"
                         data-lpignore="true"
                         v-on:keyup="fieldChange"
                         v-model="company.details.name"
                         id="new-company-name"
                         name="Company name">
                  <span class="form-field-error" v-if="this.errors.has('Company name')">{{ this.errors.first('Company name') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="new-company-parent" class="form-label color-2-63">Parent company</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': errors.has('Company name'), 'is-locked': !canEditDetails }]"
                    :disabled="canEditDetails === false"
                    :searchable=true
                    v-model="company.details.parent"
                    :options="preparedCompanies"
                    data-vv-name="Parent company"
                    data-lpignore="true"
                    name="Company status"
                    id="new-company-parent"/>
                  <span class="form-field-error" v-if="this.errors.has('Parent company')">{{ this.errors.first('Parent company') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="new-company-industry" class="form-label color-2-63">Industry</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': errors.has('Industry'), 'is-locked': !canEditDetails }]"
                    :disabled="canEditDetails === false"
                    :clearable="false"
                    :searchable=false
                    v-model="company.details.industry"
                    :options="getIndustriesForSelect"
                    data-vv-name="Industry"
                    v-validate="'required'"
                    data-lpignore="true"
                    name="Industry"
                    id="new-company-industry"/>
                  <span class="form-field-error"
                        v-if="this.errors.has('Industry')">{{ this.errors.first('Industry') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element form-element-date" id="date-selector">
                  <label for="new-company-subscription-end" class="form-label color-2-63">Subscription ends on:</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :disabled="canEditDetails === false"
                         data-lpignore="true"
                         :class="[{ 'has-error': errors.has('Subscription ends'), 'is-locked': !canEditDetails }]"
                         id="new-company-subscription-end"
                         name="Subscription ends"
                         @focus="datepicker.shown = true"
                         v-model="company.details.subscriptionEnd"
                  />
                  <div class="datepicker" v-show="canEditDetails && datepicker.shown">
                    <div class="datepicker-calendar-half">
                      <datepicker
                        :inline="true"
                        v-model="datepicker.subscription"
                        @selected="dateSelected($event, 'subscriptionEnd')"
                        :open-date="datepicker.subscription"
                        :format="'dd/MM/yyyy'"
                      />
                    </div>
                  </div>
                  <span class="form-field-error" v-if="this.errors.has('Subscription ends')">{{ this.errors.first('Subscription ends') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="new-company-status" class="form-label color-2-63">Company status</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked is-blocked"
                    :disabled="true"
                    :searchable=false
                    v-model="company.details.enabled"
                    :options="getStatusesForSelect"
                    data-vv-name="Company status"
                    data-lpignore="true"
                    :class="[{ 'has-error': errors.has('Company status'), 'is-locked': !canEditDetails }]"
                    name="Company status"
                    id="new-company-status"/>
                  <span class="form-field-error" v-if="this.errors.has('Company status')">{{ this.errors.first('Company status') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element" v-if="getUserRole === 'gm'">
                  <label for="companySizes" class="form-label color-2-63">Company size</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': this.errors.has('Company size'), 'is-locked': !canEditDetails }]"
                    :disabled="canEditDetails === false"
                    :clearable="false"
                    :searchable="false"
                    v-model="company.details.companySize"
                    :options="companySizes"
                    data-vv-name="Company size"
                    data-lpignore="true"
                    name="Company size"
                    id="companySizes"/>
                  <span class="form-field-error" v-if="this.errors.has('Company size')">{{ this.errors.first('Company size') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element" v-if="getUserRole === 'gm'">
                  <label for="new-user-types" class="form-label color-2-63">User types</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': this.errors.has('User types'), 'is-locked': !canEditDetails }]"
                    :disabled="canEditDetails === false"
                    :searchable="false"
                    :clearable="false"
                    v-model="company.details.usersType"
                    :options="getDefaultRegistrationRolesForSelect"
                    data-vv-name="User types"
                    data-lpignore="true"
                    name="User types"
                    id="new-user-types"/>
                  <span class="form-field-error" v-if="this.errors.has('User types')">{{ this.errors.first('User types') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element" v-if="getUserRole === 'gm'">
                  <label for="companyLocations" class="form-label color-2-63">Company location</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': errors.has('Company location'), 'is-locked': !canEditDetails }]"
                    :disabled="canEditDetails === false"
                    :searchable="false"
                    v-model="company.details.companyLocation"
                    :options="companyLocations"
                    data-vv-name="Company location"
                    data-lpignore="true"
                    name="Company location"
                    id="companyLocations"/>
                  <span class="form-field-error" v-if="this.errors.has('Company location')">{{ this.errors.first('Company location') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="new-company-id" class="form-label color-2-63">Company id</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         disabled="disabled"
                         :class="[{ 'is-blocked': canEditDetails, 'is-locked': !canEditDetails }]"
                         data-lpignore="true"
                         id="new-company-id"
                         name="Company id"
                         v-model="company.details.id"/>
                </div>
              </div>
              <div class="form-group form-group-half">
                <!--              <div class="form-element">
                                <input type="checkbox" class="form-control form-control__checkbox"
                                       :disabled="canEditDetails === false"
                                       data-lpignore="true"
                                       :class="[{ 'has-error': errors.has('Can export tasks'), 'is-locked': !canEditDetails }]"
                                       id="new-company-export"
                                       name="Can export tasks"
                                       v-model="company.details.can_export_tasks"/>
                                <label for="new-company-export" class="form-label color-2-63">Display tasks ids and allow exporting the
                                  list of tasks</label>
                              </div>-->
              </div>
              <div class="clearfix"></div>
              <br />
              <div class="form-group">
                <div class="form-element">
                  <input type="checkbox" class="form-control form-control__checkbox"
                         :disabled="canEditDetails === false"
                         data-lpignore="true"
                         :class="[{ 'has-error': errors.has('Sees the Chat box'), 'is-locked': !canEditDetails }]"
                         id="new-company-chat"
                         name="Sees the Chat box"
                         v-model="company.details.seesChat"/>
                  <label for="new-company-chat" class="form-label color-2-63">Sees the Chat box</label>
                </div>
                <div class="form-element" v-if="company.details.disconnectedFromBilling">
                  <input type="checkbox" class="form-control form-control__checkbox"
                         :disabled="canEditDetails === false"
                         data-lpignore="true"
                         :class="[{ 'has-error': errors.has('Demo company'), 'is-locked': !canEditDetails }]"
                         id="new-company-demo"
                         name="Demo company"
                         v-model="company.details.isDemo"/>
                  <label for="new-company-demo" class="form-label color-2-63">Demo company</label>
                </div>
                <div class="form-element">
                  <input type="checkbox" class="form-control form-control__checkbox"
                         :disabled="canEditDetails === false"
                         data-lpignore="true"
                         :class="[{ 'has-error': errors.has('Disconnected from billing'), 'is-locked': !canEditDetails }]"
                         id="new-company-disconnected-from-billing"
                         name="Disconnected from billing"
                         v-model="company.details.disconnectedFromBilling"/>
                  <label for="new-company-disconnected-from-billing" class="form-label color-2-63">Disconnected from billing</label>
                </div>
              </div>
              <div class="clearfix"></div>
              <div class="update-data update-data__details" v-if="canEditDetails">
                <button v-on:click.prevent="closeEditDetails()"
                        class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes
                </button>
                <button v-on:click.prevent="updateCompanyDetails()"
                        class="save-edit background-1-100 color-2-100 save-edit-details">Save changes
                </button>
              </div>
            </div>
            <div class="content-section content-section__bordered border-1-20 profile-section"
                 tabindex="-1"
                 ref="companyOwner">
              <h3 class="content-section-title color-1-100 background-1-100-before">
                <template v-if="canEditOwner === false">Account owner details</template>
                <template v-else>Edit account owner details</template>
              </h3>
              <button v-on:click.prevent="openEditOwner()" class="edit-details border-1-40 color-2-100"
                      v-if="canEditOwner === false">Edit
              </button>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="owner-first-name" class="form-label color-2-63">First name</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :class="[{ 'has-error': errors.has('First name'), 'is-locked': !canEditOwner }]"
                         :disabled="canEditOwner === false"
                         v-model="company.ownerData.firstName"
                         data-lpignore="true"
                         id="owner-first-name"
                         name="First name">
                  <span class="form-field-error" v-if="this.errors.has('First name')">{{ this.errors.first('First name') }}</span>
                </div>
                <div class="form-element">
                  <label for="owner-phone" class="form-label color-2-63">Phone number</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :class="[{ 'has-error': errors.has('Phone number'), 'is-locked': !canEditOwner }]"
                         :disabled="canEditOwner === false"
                         v-model="company.ownerData.phoneNumber"
                         data-lpignore="true"
                         id="owner-phone"
                         name="Phone number">
                  <span class="form-field-error" v-if="this.errors.has('Phone number')">{{ this.errors.first('Phone number') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="owner-first-name" class="form-label color-2-63">Last name</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :class="[{ 'has-error': errors.has('Last name'), 'is-locked': !canEditOwner }]"
                         :disabled="canEditOwner === false"
                         v-model="company.ownerData.lastName"
                         data-lpignore="true"
                         id="owner-last-name"
                         name="Last name">
                  <span class="form-field-error"
                        v-if="this.errors.has('Last name')">{{ this.errors.first('Last name') }}</span>
                </div>
                <div class="form-element">
                  <label for="new-user-email" class="form-label color-2-63">Email address</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :class="[{ 'has-error': errors.has('Email'), 'is-locked': !canEditOwner }]"
                         :disabled="canEditOwner === false"
                         data-lpignore="true"
                         id="new-user-email"
                         v-validate="'email'"
                         name="Email"
                         v-model="company.ownerData.email"/>
                  <span class="form-field-error" v-if="this.errors.has('Email')">{{ this.errors.first('Email') }}</span>
                </div>
              </div>
              <div class="clearfix"></div>
              <div class="update-data update-data__details" v-if="canEditOwner">
                <button v-on:click.prevent="closeEditOwner()"
                        class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes
                </button>
                <button v-on:click.prevent="updateCompanyOwner()"
                        class="save-edit background-1-100 color-2-100 save-edit-details">Save changes
                </button>
              </div>
            </div>
            <div class="content-section content-section__bordered border-1-20 profile-section"
                 v-if="getUserRole === 'ca' || getUserRole === 'gm'"
                 tabindex="-1"
                 ref="tasksSettings">
              <h3 class="content-section-title color-1-100 background-1-100-before">
                <template v-if="canEditTasksSettings === false">Tasks settings</template>
                <template v-else>Edit tasks settings</template>
              </h3>
              <button v-on:click.prevent="openTasksSettings()" class="edit-details border-1-40 color-2-100"
                      v-if="canEditTasksSettings === false">Edit
              </button>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="company-contraption" class="form-label color-2-63">Company contraption</label>
                  <input type="text"
                         class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                         :disabled="canEditTasksSettings === false"
                         data-lpignore="true"
                         maxlength="4"
                         style="text-transform:uppercase"
                         :class="[{ 'has-error': errors.has('Company contraption'), 'is-locked': !canEditTasksSettings }]"
                         id="company-contraption"
                         name="Company contraption"
                         v-model="company.tasksSettings.contraption"/>
                </div>
              </div>
              <div class="clearfix" v-if="getUserRole === 'gm'"></div>
              <div class="form-group form-group-half">
                <div class="form-element">
                  <label for="company-default-assignee" class="form-label color-2-63">Default assignee</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': errors.has('Default assignee'), 'is-locked': !canEditTasksSettings }]"
                    :disabled="canEditTasksSettings === false"
                    :searchable=true
                    v-model="company.tasksSettings.defaultAssignee"
                    :options="defaultAssigneeForSelect"
                    data-vv-name="Default assignee"
                    data-lpignore="true"
                    name="Default assignee"
                    id="company-default-assignee"/>
                  <span class="form-field-error" v-if="this.errors.has('Default assignee')">{{ this.errors.first('Default assignee') }}</span>
                </div>
              </div>
              <div class="form-group form-group-half" v-if="getUserRole === 'gm'">
                <div class="form-element">
                  <label for="company-account-manager" class="form-label color-2-63">Account manager</label>
                  <v-select
                    class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                    :class="[{ 'has-error': errors.has('Account manager'), 'is-locked': !canEditTasksSettings }]"
                    :disabled="canEditTasksSettings === false"
                    :searchable=true
                    v-model="company.tasksSettings.accountManager"
                    :options="adminUsersForSelect"
                    data-vv-name="Account manager"
                    data-lpignore="true"
                    name="Account manager"
                    id="company-account-manager"/>
                  <span class="form-field-error" v-if="this.errors.has('Account manager')">{{ this.errors.first('Account manager') }}</span>
                </div>
              </div>
              <div class="clearfix"></div>
              <div class="update-data update-data__details" v-if="canEditTasksSettings">
                <button v-on:click.prevent="closeEditTasksSettings()"
                        class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes
                </button>
                <button v-on:click.prevent="updateCompanyTasksSettings()"
                        class="save-edit background-1-100 color-2-100 save-edit-details">Save changes
                </button>
              </div>
            </div>
            <div class="content-section content-section__bordered border-1-20 profile-section"
                 tabindex="-1"
                 ref="companyRegistration">
              <h3 class="content-section-title color-1-100 background-1-100-before">
                <template v-if="canEditCompanyRegistration === false">User registration settings</template>
                <template v-else>Edit user registration settings</template>
              </h3>
              <button v-on:click.prevent="openEditCompanyRegistration()" class="edit-details border-1-40 color-2-100"
                      v-if="canEditCompanyRegistration === false">Edit
              </button>
              <div class="form-group">
                <div class="form-group form-group-half">
                  <div class="form-element">
                    <label for="company-registration" class="form-label color-2-63">Allow user registration</label>
                    <v-select
                      class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                      :disabled="!canEditCompanyRegistration"
                      :searchable=false
                      v-model="company.details.allowRegistration"
                      :options="yesNoOptionsForSelect"
                      data-vv-name="Allow user registration"
                      data-lpignore="true"
                      :class="[{ 'has-error': errors.has('Allow user registration'), 'is-locked is-blocked': !canEditCompanyRegistration }]"
                      name="Allow user registration"
                      id="company-registration" />
                    <span class="form-field-error" v-if="this.errors.has('Allow user registration')">{{ this.errors.first('Allow user registration') }}</span>
                  </div>
                </div>
                <div class="form-group form-group-half" v-if="company.details.allowRegistration && company.details.allowRegistration.value">
                  <div class="form-element">
                    <label for="new-company-url" class="form-label color-2-63">Company URL</label>
                    <input type="text" class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                           :disabled="canEditCompanyRegistration === false"
                           data-lpignore="true"
                           :class="[{ 'has-error': errors.has('Company url'), 'is-locked': !canEditCompanyRegistration }]"
                           id="new-company-url"
                           v-validate="'required'"
                           name="Company url"
                           v-model="company.details.url"/>
                  </div>
                </div>
                <div class="form-group form-group-half" v-if="company.details.allowRegistration && company.details.allowRegistration.value">
                  <div class="form-element">
                    <label for="company-registration-all-emails" class="form-label color-2-63">Only allow emails from an approved domain</label>
                    <v-select
                      class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                      :disabled="!canEditCompanyRegistration"
                      :searchable=false
                      v-model="company.details.allowAllEmails"
                      :options="yesNoOptionsForSelect"
                      data-vv-name="Allow all emails"
                      data-lpignore="true"
                      :class="[{ 'has-error': errors.has('Allow all emails'), 'is-locked is-blocked': !canEditCompanyRegistration }]"
                      name="Allow all emails"
                      id="company-registration-all-emails" />
                    <span class="form-field-error" v-if="this.errors.has('Allow all emails')">{{ this.errors.first('Allow all emails') }}</span>
                  </div>
                </div>
                <div class="form-group form-group-half" v-if="company.details.allowRegistration && company.details.allowRegistration.value">
                  <div class="form-element">
                    <label for="company-registration-need-approval" class="form-label color-2-63">New users must be approved by an admin</label>
                    <v-select
                      class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                      :disabled="!canEditCompanyRegistration || (company.details.allowRegistration && !company.details.allowRegistration.value)"
                      :searchable=false
                      v-model="company.details.emailApproval"
                      :options="yesNoOptionsForSelectNew"
                      data-vv-name="Request approval"
                      data-lpignore="true"
                      :class="[{ 'has-error': errors.has('Request approval'), 'is-locked is-blocked': !canEditCompanyRegistration }]"
                      name="Request approval"
                      id="company-registration-need-approval" />
                    <span class="form-field-error" v-if="this.errors.has('Allow all emails')">{{ this.errors.first('Request approval') }}</span>
                  </div>
                </div>
              </div>
              <div class="clearfix"></div>
              <div  v-if="company.details.allowRegistration && company.details.allowRegistration.value" >
                <h3 class="content-section-subtitle color-2-63">Approved email domains</h3>
                <div class="form-group">
                  <div class="form-group form-group-half"
                       v-if="canEditCompanyRegistration === true">
                    <div class="form-element">
                      <label class="form-label color-2-63" for="pasteEmails">Bulk paste</label>
                      <textarea data-lpignore="true"
                                class="form-control form-control__textarea"
                                rows="2"
                                id="pasteEmails"
                                cols="20"
                                v-model="emailControl['input']"
                                @paste="emailControl['isPaste'] = true"
                                v-on:blur="createTags($event, false, true)"
                                v-on:input="createTags($event, true)"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="form-group form-group-half"
                     v-for="(row, index) in company.approvedEmails"
                     :class="[{ 'form-group-right-item': (index > 0 && (index + 1) % 2 === 0) && canEditCompanyRegistration === true, 'form-group__email-ending-input' : canEditCompanyRegistration === true }]">
                  <div class="form-element">
                    <input type="text"
                           title="Email"
                           aria-label="Email"
                           class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                           :disabled="canEditCompanyRegistration === false"
                           v-model="row.email"
                           data-lpignore="true"/>
                  </div>
                  <div class="form-group form-group-half form-group__email-ending-button form-group__email-ending-remove"
                       v-if="canEditCompanyRegistration === true">
                    <div class="add-email-ending" v-on:click="removeElement(index)"></div>
                  </div>
                </div>
                <!--<div class="clearfix"></div>-->
                <div class="form-group form-group-half form-group__email-add-button"
                     v-if="canEditCompanyRegistration === true">
                  <div class="add-email-ending" @click="addEmailRow">Add new</div>
                </div>
              </div>
              <div class="clearfix"></div>
              <div class="update-data update-data__details" v-if="canEditCompanyRegistration">
                <button v-on:click.prevent="closeEditCompanyRegistration()"
                        class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes
                </button>
                <button v-on:click.prevent="updateCompanyRegistration()"
                        class="save-edit background-1-100 color-2-100 save-edit-details">Save changes
                </button>
              </div>
            </div>
          </template>
          <template v-else-if="this.tabActive === 'billing'">
            <billing-profile v-bind:embedded="true" v-bind:embedded-company-id="companyId"></billing-profile>
          </template>
          <template v-else-if="this.tabActive === 'progress'">
            <div class="content-section profile-section no-margin-top"
                 tabindex="-1"
                 ref="companyChart"
                 :class="[{ 'content-section__bordered border-1-20': activeSection === 'companyChart'}]"
                 v-bind:style="{ display: !hasPoints ? 'none' : '' }">
              <div class="content-section-title color-1-100 background-1-100-before">
                <h3>Company progress</h3>
                <div class="content-company-badge"
                     id="contentCompanyBadge"
                     @click.prevent="badgeCopy = true"
                     v-if="user">
                  <iframe width="210" height="85"
                          :src="getBadgeLink() + '?company=' + (company ? company.hash : '') + '&theme=' + (selectedHightCont === 1 ? 'dark' : 'light')"></iframe>
                </div>
              </div>
              <div class="content-chart-date">{{ getCurrentDate() }}</div>
              <div class="content-chart-wrapper">
                <div class="content-chart-legend-y">
                  <div class="content-chart-legend-y__item level-5"><span>Platinum</span></div>
                  <div class="content-chart-legend-y__item level-4"><span>Gold</span></div>
                  <div class="content-chart-legend-y__item level-3"><span>Silver</span></div>
                  <div class="content-chart-legend-y__item level-2"><span>Bronze</span></div>
                  <div class="content-chart-legend-y__item level-1"><span>Foundation</span></div>
                </div>
                <div class="content-chart-body">
                  <div v-bind:style="{ width: getChartWidth(chartData, true) }">
                    <div class="content-chart-data" v-bind:style="{ width: getChartWidth(chartData, true) }">
                      <div class="content-loading"
                           v-if="!chartData">
                      </div>
                      <div class="content-chart-grid">
                        <div class="content-chart-grid-line line_10"></div>
                        <div class="content-chart-grid-line line_9"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_8"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_7"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_6"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_5"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_4"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_3"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_2"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                        <div class="content-chart-grid-line line_1"
                             v-bind:style="{ width: getChartWidth(chartData, false) }"></div>
                      </div>
                      <div class="content-chart-points"
                           v-if="c"
                           v-for="(c, index) in chartData">
                        <div class="content-chart-point"
                             :class="'chapter__' + c.chapter.id"
                             :data-chapter="'chapter-legend-' + c.chapter.id"
                             :data-color="c.chapter.color"
                             v-bind:style="{left: p.left + 'px', top: p.top + '%', borderColor: c.chapter.color }"
                             v-for="p in c.scores">
                        </div>
                        <div class="content-chart-chapters__item"
                             :class="'chapter_' + getChapterIndex(c)"
                             @click.prevent="addChapterHover(c, 'level')"
                             :id="'chapter-legend-' + c.chapter.id"
                             v-bind:style="{
                           backgroundColor: c.hover && c.hover.type === 'level' && c.hover.show === true ? c.chapter.color : '',
                           color: selectedHightCont === 1 ? (c.hover && c.hover.type === 'level' && c.hover.show === true ? '#000000' : c.chapter.color) : c.hover && c.hover.type === 'level' && c.hover.show === true ? '#000000' : '',
                           borderColor: c.chapter.color
                         }">
                          {{ getChapterCustomName(c.chapter) }}
                        </div>
                        <span class="show-benchmark tooltip"
                              v-if="hasBenchmark(c)"
                              @click.prevent="addChapterHover(c, 'benchmark')"
                              :class="[{ 'active': c.hover && c.hover.type === 'benchmark' && c.hover.show === true }, 'chapter_' + getChapterIndex(c)]">
                      <span class="tooltip-text">Show benchmark</span>
                    </span>
                        <div class="chapter-level"
                             v-bind:style="{ top: (100 - c.hover.top) + '%', height: c.hover.top + '%'}"
                             v-if="c.hover && c.hover.type === 'level' && c.hover.show === true"></div>
                        <div class="chapter-benchmark"
                             v-bind:style="{ top: (100 - c.hover.top) + '%', height: c.hover.top + '%'}"
                             v-if="c.hover && c.hover.type === 'benchmark' && c.hover.show === true"></div>
                      </div>
                    </div>
                    <div class="content-chart-legend-x" v-bind:style="{ width: getChartWidth(chartData, true) }">
                      <div class="content-chart-legend-x__item" v-for="quarter in quarters">
                        Q{{ quarter.quarter }} {{ quarter.year }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="content-section content-section__bordered border-1-20 profile-section"
                 tabindex="-1"
                 ref="companyProgress">
              <h3 class="content-section-title color-1-100 background-1-100-before">
                <template v-if="canEditProgressChapters === false">Company progress on task categories</template>
                <template v-else>Edit company progress on task categories</template>
              </h3>
              <button v-on:click.prevent="openProgressChapters()" class="edit-details border-1-40 color-2-100"
                      v-if="canEditProgressChapters  === false">Edit
              </button>
              <div class="form-group progress-table">
                <table class="benchmark-table">
                  <thead>
                  <tr>
                    <th class="has-order">
                      <div @click.stop="changeOrder()">
                        <div class="caption-head-text">
                          <span class="caption-head-inner-text">Quarters</span>
                        </div>
                        <div class="caption-head-order-change">
                          <div class="caption-head-order-up"
                               :class="{'active': this.chapterOrderBy !== null && this.chapterOrderBy.order}"></div>
                          <div class="caption-head-order-down"
                               :class="{'active': this.chapterOrderBy !== null && !this.chapterOrderBy.order}"></div>
                        </div>
                      </div>
                    </th>
                    <th>Policies and procedures</th>
                    <th>Finding talent</th>
                    <th>Assessing talent</th>
                    <th>Retaining talent</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(row, index) in editedChapters">
                    <td class="name">Q{{ row.quarter }} {{ row.year }}</td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditProgressChapters === false}]">
                        <input
                          title="Policies and procedures score"
                          type="number"
                          class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                          min="0"
                          max="100"
                          :disabled="canEditProgressChapters === false"
                          v-model="row.score.policies"
                          data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditProgressChapters === false}]">
                        <input
                          title="Finding talent score"
                          type="number"
                          class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                          min="0"
                          max="100"
                          :disabled="canEditProgressChapters === false"
                          v-model="row.score.finding"
                          data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditProgressChapters === false}]">
                        <input
                          title="Assessing talent score"
                          type="number"
                          class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                          min="0"
                          max="100"
                          :disabled="canEditProgressChapters === false"
                          v-model="row.score.assessing"
                          data-lpignore="true">
                      </div>
                    </td>
                    <td>
                      <div :class="[{ 'is-disabled': canEditProgressChapters === false}]">
                        <input
                          title="Retaining talent score"
                          type="number"
                          class="form-control form-control__input background-1-10-disabled  border-1-10-disabled"
                          min="0"
                          max="100"
                          :disabled="canEditProgressChapters === false"
                          v-model="row.score.retaining"
                          data-lpignore="true">
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div class="clearfix"></div>
              <div class="update-data update-data__details" v-if="canEditProgressChapters">
                <button v-on:click.prevent="closeEditProgressChapters()"
                        class="cancel-edit background-2-7 color-2-100 cancel-edit-details">Discard changes
                </button>
                <button v-on:click.prevent="updateProgressChapters()"
                        class="save-edit background-1-100 color-2-100 save-edit-details">Save changes
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="badge-copy" v-if="badgeCopy === true">
        <div class="badge-copy-container">
          <div class="badge-copy-close" @click.prevent="badgeCopy = false"></div>
          <div class="badge-copy-selector">
            <div class="form-element">
              <label for="iframe-themes" class="form-label color-2-63">Theme</label>
              <v-select
                class="form-control form-control__select background-1-10-child  border-1-10-child background-2-10-locked border-2-10-locked"
                :searchable=true
                v-model="iframeTheme"
                :options="iframeThemes"
                data-lpignore="true"
                id="iframe-themes"/>
            </div>
          </div>
          <div class="badge-copy-iframe" :class="iframeTheme.value">
            <iframe width="210" height="85"
                    id="previewIframe"
                    :src="getBadgeLink() + '?company=' + (company ? company.hash : '') + '&theme=' + iframeTheme.value"></iframe>
          </div>
          <div class="badge-copy-code">
            <button @click.prevent="copyIframe()">Copy</button>
            <textarea
              title="Badge code"
              id="iframeCode"
              rows="3"
              cols="50"
              onclick="this.focus(); this.select()"
              v-html="getIframeCode()"
              readonly="readonly"></textarea>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- END:.profile -->
</template>

<script>
  import api from '@/api'
  import Vue from 'vue'
  import {mapActions, mapGetters} from 'vuex'
  import AppSelect from '@/components/admin/common/Select'
  import BillingProfile from './BillingProfile'

  export default {
    name: 'edit-company',
    data () {
      return {
        disableAPI: false,
        infMsgTitle: '',
        infMsgDescription: '',
        infMsgClass: '',
        messageDisplay: false,
        canEditDetails: false,
        canEditTasksSettings: false,
        canEditCompanyRegistration: false,
        canEditOwner: false,
        canEditProgressChapters: false,
        confOpened: null,
        statusPayload: null,
        company: {
          details: {
            id: '',
            name: '',
            parent: '',
            industry: null,
            subscriptionEnd: null,
            enabled: {
              label: '',
              value: ''
            },
            url: null,
            // can_export_tasks: false,
            seesChat: true,
            isDemo: false,
            disconnectedFromBilling: true,
            allowRegistration: null,
            allowAllEmails: null,
            emailApproval: null,
            usersType: null,
            companySize: null,
            companyLocation: null
          },
          tasksSettings: {
            contraption: '',
            defaultAssignee: false,
            accountManager: false
          },
          approvedEmails: [
            {
              email: ''
            }
          ],
          ownerData: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: ''
          },
          progressDetails: {},
          progressChapters: {}
        },
        initialData: {
          details: {
            id: '',
            name: '',
            parent: '',
            industry: null,
            subscriptionEnd: null,
            enabled: {
              label: '',
              value: ''
            },
            url: 'clearassured.CompanyName.com',
            // can_export_tasks: false,
            seesChat: true,
            isDemo: false,
            disconnectedFromBilling: true,
            allowRegistration: false,
            allowAllEmails: false,
            emailApproval: false,
            usersType: null,
            companySize: null,
            companyLocation: null
          },
          tasksSettings: {
            contraption: '',
            defaultAssignee: false,
            accountManager: false
          },
          approvedEmails: [
            {
              email: ''
            }
          ],
          ownerData: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: ''
          },
          progressDetails: {},
          progressChapters: {}
        },
        confirmation: {
          action: '',
          isShown: false,
          articleId: null,
          i: null,
          style: {
            top: '100px'
          }
        },
        canLeave: false,
        next: null,
        isChanged: false,
        isStakeholder: false,
        tabActive: 'profile',
        changeUrl: false,
        companyId: null,
        datepicker: {
          subscription: null,
          startDate: null,
          shown: false,
          startShown: false,
          keepFocus: false
        },
        dataFound: true,
        editedChapters: [],
        chapterOrderBy: {
          order: false
        },
        quarters: {},
        chapters: [],
        articles: [],
        user: {},
        dataLoading: false,
        dataLoaded: false,
        activeSection: 'companyDetails',
        hasPoints: false,
        chartData: null,
        badgeCopy: false,
        iframeTheme: {
          label: 'Light',
          value: 'light'
        },
        industry: null,
        emailControl: {
          input: '',
          isPaste: false
        },
        disconnectedFromBillingCheckConfirmed: false
      }
    },
    computed: {
      ...mapGetters([
        'selectedHightCont',
        'getCompaniesForSelect',
        'getIndustriesForSelect',
        'getUserRole',
        'getContentChapterForSelect',
        'getConfData',
        'defaultAssigneeForSelect',
        'adminUsersForSelect',
        'companySizes',
        'companyLocations',
        'getDefaultRegistrationRolesForSelect'
      ]),
      getStatusesForSelect () {
        return [
          {
            label: 'Active',
            value: 2
          },
          {
            label: 'Inactive',
            value: 1
          },
          {
            label: 'Archived',
            value: 0
          }
        ]
      },
      yesNoOptionsForSelect () {
        return [
          {
            label: 'Yes',
            value: true
          },
          {
            label: 'No',
            value: false
          }
        ]
      },
      yesNoOptionsForSelectNew () {
        return [
          {
            label: this.company.details.allowAllEmails && this.company.details.allowAllEmails.value ? 'Yes' : 'Yes, all',
            value: true
          },
          {
            label: this.company.details.allowAllEmails && this.company.details.allowAllEmails.value ? 'No' : 'Only accounts without an approved domain email',
            value: false
          }
        ]
      },
      preparedCompanies () {
        let companyList = []
        for (let company in this.getCompaniesForSelect) {
          if (this.getCompaniesForSelect[company].value !== this.companyId * 1 && this.getCompaniesForSelect[company].parent !== 1) {
            companyList.push(this.getCompaniesForSelect[company])
          }
        }
        return companyList
      },
      getQuarters () {
        let self = this
        let todayDate = new Date()
        let todayYear = todayDate.getFullYear()
        let todayMonth = todayDate.getMonth()
        let startDate = this.datepicker.startDate ? new Date(this.datepicker.startDate) : new Date()
        let startYear = startDate.getFullYear()
        let startMonth = startDate.getMonth()
        let quarterNames = ['Q1', 'Q2', 'Q3', 'Q4']
        let quarters = []
        for (let y = startYear; y < todayYear; y++) {
          // let dataForThisQuarter = this.quarters.find(item => item.quarter === res.body.enabled)
          let lastYearQuarters
          if (y > startYear) {
            lastYearQuarters = quarterNames.slice(0, 4)
          } else {
            lastYearQuarters = quarterNames.slice(parseInt(startMonth / 3), 4)
          }
          lastYearQuarters.forEach(function (index, data) {
            let dataForThisQuarter = self.quarters[y.toString() + index.slice(1, 2)]
            if (dataForThisQuarter) {
              quarters.push({
                quarter: index.slice(1, 2),
                year: y,
                policies: dataForThisQuarter.score.policies,
                finding: dataForThisQuarter.score.finding,
                assessing: dataForThisQuarter.score.assessing,
                retaining: dataForThisQuarter.score.retaining
              })
            } else {
              quarters.push({
                quarter: index.slice(1, 2),
                year: y,
                policies: 0,
                finding: 0,
                assessing: 0,
                retaining: 0
              })
            }
          })
        }
        let startIndex = startYear === todayYear ? Math.ceil((startMonth + 1) / 3) - 1 : 0
        let thisYearQuarters = quarterNames.slice(startIndex, parseInt(todayMonth / 3 + 1))
        thisYearQuarters.forEach(function (index, data) {
          let dataForThisQuarter = self.quarters[todayYear.toString() + index.slice(1, 2)]
          if (dataForThisQuarter) {
            quarters.push({
              quarter: index.slice(1, 2),
              year: todayYear,
              policies: dataForThisQuarter.score.policies,
              finding: dataForThisQuarter.score.finding,
              assessing: dataForThisQuarter.score.assessing,
              retaining: dataForThisQuarter.score.retaining
            })
          } else {
            quarters.push({
              quarter: index.slice(1, 2),
              year: todayYear,
              policies: 0,
              finding: 0,
              assessing: 0,
              retaining: 0
            })
          }
        })
        return quarters
      },
      filteredChapters () {
        let result = this.getQuarters
        let ascDesc = this.chapterOrderBy.order ? 1 : -1
        return result.sort((a, b) => ascDesc * (parseInt(a.year + '' + a.quarter) - parseInt(b.year + '' + b.quarter)))
      }
    },
    methods: {
      ...mapActions([
        'clearConfAction',
        'getCompanies',
        'getIndustries',
        'setInformationMsg',
        'getChapters',
        'initConfAction',
        'getTaskAssignee',
        'getAdminUsers',
        'getCompanySizes',
        'getCompanyLocations',
        'getDefaultRegistrationRoles'
      ]),
      openEditCompanyRegistration: function () {
        this.canEditCompanyRegistration = true
      },
      openEditDetails: function () {
        this.canEditDetails = true
      },
      openTasksSettings: function () {
        this.canEditTasksSettings = true
      },
      openEditOwner: function () {
        this.canEditOwner = true
      },
      openProgressChapters: function () {
        this.canEditProgressChapters = true
      },
      closeEditCompanyRegistration: function () {
        this.company.approvedEmails = this.initialData.approvedEmails
        this.canEditCompanyRegistration = false
        this.errors.clean()
      },
      closeEditDetails: function () {
        this.company.details = this.initialData.details
        this.canEditDetails = false
        this.errors.clean()
      },
      closeEditTasksSettings: function () {
        this.company.tasksSettings = this.initialData.tasksSettings
        this.canEditTasksSettings = false
        this.errors.clean()
      },
      closeEditOwner: function () {
        this.company.ownerData = this.initialData.ownerData
        this.canEditOwner = false
        this.errors.clean()
      },
      closeEditProgressChapters: function () {
        this.company.progressChapters = this.initialData.progressChapters
        this.canEditProgressChapters = false
      },
      closeMsg: function () {
        this.errors.clear()
      },
      deleteProfile () {
        this.confOpened = 'delete'
        this.initConfAction({
          actionName: 'removeCompanyById'
        })
      },
      resetTaskHistory () {
        this.confOpened = 'reset'
        this.initConfAction({
          actionName: 'resetTaskHistoryById'
        })
      },
      deleteCompanyConfirmed () {
        let id = this.companyId
        this.$http.delete(api.URLS.companyList + '/' + id, api.headersAuthSettings)
          .then((res) => {
            this.infMsgTitle = this.initialData.details.name + ' company was successfully deleted.'
            this.infMsgDescription = 'All users and stored information belonging to ' + this.initialData.details.name + ' were also deleted.'
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.goToCompanyList()
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We can\'t delete this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            if (this.getUserRole === 'gm') {
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
            } else {
              this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            }
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            console.log(err)
          })
        this.getConfData.actionConfirmed = false
      },
      resetTaskHistoryConfirmed () {
        let id = this.companyId
        this.$http.put(api.URLS.task + '/reset/' + id, {}, api.headersAuthSettings)
          .then((res) => {
            this.infMsgTitle = this.initialData.details.name + ' company task history was successfully reset.'
            this.infMsgDescription = 'All tasks and tasks history belonging to ' + this.initialData.details.name + ' were also reset.'
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We can\'t reset tasks for this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            if (this.getUserRole === 'gm') {
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
            } else {
              this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            }
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            console.log(err)
          })
        this.getConfData.actionConfirmed = false
      },
      archiveProfile () {
        this.confOpened = 'archive'
        this.initConfAction({
          actionName: 'changeCompanyStatus',
          userStatus: this.company.details.enabled.value ? 0 : 1
        })
      },
      archiveCompanyConfirmed () {
        let companyData = {
          id: this.companyId,
          enabled: !this.company.details.enabled.value
        }
        this.$http.put(api.URLS.companyCreate, companyData, api.headersAuthSettings)
          .then((res) => {
            let actionText = res.body.enabled ? 'Unarchived' : 'Archived'
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' company  profile was successfully ' + actionText + '.'
            if (res.body.enabled) {
              this.infMsgDescription = 'All active users belonging to ' + res.body.name + ' will now be able to login.'
            } else {
              this.infMsgDescription = 'All users belonging to ' + res.body.name + ' will no longer be able to login. However, the company will still appear in the list of companies and all company data will be kept in the database.'
            }
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We can\'t archive this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            if (this.getUserRole === 'gm') {
              this.infMsgDescription = 'If you need assistance, please send an email to tcc@eromagency.com in the following format: “I was on this page (insert the URL of the page where you got this error), did this action, was expecting to get this result, but got this error instead. It happened at this time on this date, while using this operating system and this browser (please insert browser version as well - usually it can be found in browser menu → Help → About), being logged in with this user. On a scale from 1 to 10 (where 1 means there is no rush and 10 means I need it yesterday), I need a solution for this with this priority”. Thank you.'
            } else {
              this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            }
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            console.log('err', err)
            this.getCompanyData()
          })
        this.getConfData.actionConfirmed = false
      },
      changeUrlConfirmed () {
        this.changeUrl = true
      },
      fieldChange () {
        this.isChanged = true
      },
      getCompanyData () {
        this.$http.put(api.URLS.companyCreate, {id: this.companyId}, api.headersAuthSettings).then((res) => {
          let companyDetails = this.company
          // set received data
          companyDetails.details.id = res.body.id
          // organisation details
          companyDetails.details.name = res.body.name
          companyDetails.details.subscriptionEnd = this.convertDate(res.body.subscription_end, '/')
          companyDetails.details.enabled = this.getStatusesForSelect.find(item => item.value === this.getCompanyStatus(res.body).value)
          companyDetails.details.url = res.body.url
          // companyDetails.details.can_export_tasks = res.body.can_export_tasks
          companyDetails.details.seesChat = res.body.sees_chat
          companyDetails.details.isDemo = res.body.is_demo
          companyDetails.details.disconnectedFromBilling = res.body.disconnected_from_billing === 2 || res.body.disconnected_from_billing === 3
          companyDetails.tasksSettings.contraption = res.body.contraption
          companyDetails.tasksSettings.defaultAssignee = null
          if (res.body.default_assignee !== null) {
            companyDetails.tasksSettings.defaultAssignee = {
              id: res.body.default_assignee.id,
              label: res.body.default_assignee.first_name + ' ' + res.body.default_assignee.last_name,
              value: res.body.default_assignee.id
            }
          }
          companyDetails.tasksSettings.accountManager = null
          if (res.body.account_manager !== null) {
            companyDetails.tasksSettings.accountManager = {
              id: res.body.account_manager.id,
              label: res.body.account_manager.first_name + ' ' + res.body.account_manager.last_name,
              value: res.body.account_manager.id
            }
          }
          companyDetails.hash = res.body.hash
          // company email endings
          companyDetails.approvedEmails = res.body.email_endings !== null ? JSON.parse(res.body.email_endings) : companyDetails.approvedEmails
          // company owner data
          companyDetails.ownerData.firstName = res.body.owner_first_name
          companyDetails.ownerData.lastName = res.body.owner_last_name
          companyDetails.ownerData.phoneNumber = res.body.owner_phone_number
          companyDetails.ownerData.email = res.body.owner_email
          companyDetails.progressDetails.startDate = res.body.created ? this.convertDate(res.body.created, '/') : this.convertDate(res.body.start_date, '/')
          companyDetails.points = res.body.points
          companyDetails.details.parent = null
          if (res.body.parent !== null) {
            companyDetails.details.parent = {
              id: res.body.parent.id,
              label: res.body.parent.name,
              value: res.body.parent.id
            }
          }
          companyDetails.details.usersType = null
          if (res.body.users_type !== null) {
            companyDetails.details.usersType = {
              label: res.body.users_type.name,
              value: res.body.users_type.id,
              id: res.body.users_type.id
            }
          }
          companyDetails.details.companySize = null
          if (res.body.company_size !== null) {
            companyDetails.details.companySize = {
              label: res.body.company_size.name,
              value: res.body.company_size.id,
              id: res.body.company_size.id
            }
          }
          companyDetails.details.companyLocation = null
          if (res.body.company_location !== null) {
            companyDetails.details.companyLocation = {
              label: res.body.company_location.name,
              value: res.body.company_location.id,
              id: res.body.company_location.id
            }
          }
          companyDetails.details.industry = null
          if (res.body.industry !== null) {
            this.industry = res.body.industry
            companyDetails.details.industry = {
              id: res.body.industry.id,
              label: res.body.industry.name,
              value: res.body.industry.id
            }
            if (res.body.industry.landing) {
              companyDetails.details.industry.landing = {
                slug: res.body.industry.landing.slug
              }
            }
          }
          companyDetails.details.allowRegistration = null
          if (res.body.allow_registration !== null) {
            companyDetails.details.allowRegistration = this.yesNoOptionsForSelect.find((item) => item.value === res.body.allow_registration)
          }
          companyDetails.details.allowAllEmails = null
          if (res.body.allow_all_emails !== null) {
            companyDetails.details.allowAllEmails = this.yesNoOptionsForSelect.find((item) => item.value === !res.body.allow_all_emails)
          }
          companyDetails.details.emailApproval = null
          if (res.body.email_approval !== null) {
            companyDetails.details.emailApproval = this.yesNoOptionsForSelectNew.find((item) => item.value === res.body.email_approval)
          }
          // set datepicker
          this.datepicker.subscription = res.body.subscription_end
          this.datepicker.startDate = res.body.created ? res.body.created : res.body.start_date
          // set initial data
          this.initialData = companyDetails
          document.title = res.body.name + '\'s details | Clear Assured'
          if (window._paq) {
            window._paq.push(['setCustomUrl', location.pathname])
            window._paq.push(['setDocumentTitle', document.title])
            window._paq.push(['trackPageView'])
          }
          // if (window.ga) {
          //   window.ga('set', 'contentGroup1', 'ADMINISTRATION_AREA')
          //   window.ga('send', 'pageview', {
          //     'page': location.pathname,
          //     'location': location.href,
          //     'title': document.title
          //   })
          // }
        }).catch((err) => {
          if (err.body.error.code === 404) {
            let props = this.$router.resolve({
              name: 'error'
            })
            location.href = props.href
          }
          if (err.status === 401) {
            this.authLogout()
          }
        })
      },
      getCompanyPoints () {
        this.$http.get(api.URLS.companyPoints + '/' + this.companyId, api.headersAuthSettings).then((res) => {
          this.quarters = res.body
          if (this.quarters) {
            this.hasPoints = true
          }
        }).catch((err) => {
          console.log(err)
        })
      },
      updateCompanyDetails () {
        if (this.disableAPI) return
        this.$validator.validateAll()
        if (this.errors.items.length) return
        let companyData = {
          id: this.company.details.id,
          companyName: this.company.details.name,
          subscriptionEnd: this.company.details.subscriptionEnd,
          enabled: this.company.details.enabled,
          seesChat: this.company.details.seesChat,
          isDemo: this.company.details.isDemo,
          disconnectedFromBilling: this.company.details.disconnectedFromBilling ? 3 : 0
        }
        if (this.company.details.parent !== null) {
          companyData.parent = this.company.details.parent
        } else {
          companyData.parent = {}
        }
        if (this.company.details.industry !== null) {
          companyData.industry = this.company.details.industry
        } else {
          companyData.industry = {}
        }
        if (this.company.details.usersType !== null) {
          companyData.usersType = this.company.details.usersType
        } else {
          companyData.usersType = {}
        }
        if (this.company.details.companySize !== null) {
          companyData.companySize = this.company.details.companySize
        } else {
          companyData.companySize = {}
        }
        if (this.company.details.companyLocation !== null) {
          companyData.companyLocation = this.company.details.companyLocation
        } else {
          companyData.companyLocation = {}
        }
        this.disableAPI = true
        this.$http.put(api.URLS.companyCreate, companyData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' organisation details was successfully updated.'
            this.infMsgDescription = 'Please note all edits are being logged. '
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
            this.canEditDetails = !this.canEditDetails
          })
          .catch((err) => {
            this.disableAPI = false
            console.log('err', err)
            this.infMsgTitle = 'Error. We were not able to update this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
          })
      },
      updateCompanyTasksSettings () {
        if (this.disableAPI) return
        this.$validator.validateAll()
        if (this.errors.items.length) return
        let companyData = {
          id: this.company.details.id,
          contraption: this.company.tasksSettings.contraption,
          defaultAssignee: this.company.tasksSettings.defaultAssignee || false,
          accountManager: this.company.tasksSettings.accountManager || false
        }
        this.disableAPI = true
        this.$http.put(api.URLS.companyCreate, companyData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' company tasks settings was successfully updated.'
            this.infMsgDescription = 'Please note all edits are being logged. '
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
            this.canEditTasksSettings = !this.canEditTasksSettings
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We were not able to update this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
          })
      },
      updateCompanyRegistration () {
        this.$validator.validateAll()
        if (this.errors.items.length) return
        if (this.disableAPI) return
        let registrationData = {
          id: this.company.details.id,
          emailEndings: this.company.approvedEmails,
          url: this.company.details.url,
          allowRegistration: this.company.details.allowRegistration ? this.company.details.allowRegistration.value === true : false,
          allowAllEmails: this.company.details.allowAllEmails ? this.company.details.allowAllEmails.value === false : true,
          emailApproval: this.company.details.emailApproval ? this.company.details.emailApproval.value === true : false
        }
        this.disableAPI = true
        this.$http.put(api.URLS.companyCreate, registrationData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' user registration settings was successfully updated.'
            this.infMsgDescription = 'Please note all edits are being logged.'
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.canEditCompanyRegistration = false
            this.getCompanyData()
          })
          .catch((err) => {
            this.getCompanyData()
            this.disableAPI = false
            this.infMsgTitle = 'Error. We were not able to update this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.canEditCompanyRegistration = false
          })
        this.messageDisplay = true
      },
      updateCompanyOwner () {
        if (this.disableAPI) return
        this.$validator.validateAll()
        if (this.errors.items.length) return
        let owner = {
          id: this.company.details.id,
          ownerFirstName: this.company.ownerData.firstName,
          ownerLastName: this.company.ownerData.lastName,
          ownerPhoneNumber: this.company.ownerData.phoneNumber,
          ownerEmail: this.company.ownerData.email
        }
        this.disableAPI = true
        this.$http.put(api.URLS.companyCreate, owner, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' company owner details was successfully updated.'
            this.infMsgDescription = 'Please note all edits are being logged.'
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
            this.canEditOwner = !this.canEditOwner
          })
          .catch((err) => {
            this.disableAPI = false
            console.log('err', err)
            this.infMsgTitle = 'Error. We were not able to update this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyData()
          })
      },
      updateProgressChapters () {
        if (this.disableAPI) return
        let chaptersData = {
          id: this.companyId,
          quarter: this.editedChapters
        }
        this.disableAPI = true
        this.$http.put(api.URLS.companyCreate, chaptersData, api.headersAuthSettings)
          .then((res) => {
            this.disableAPI = false
            this.infMsgTitle = res.body.name + ' company progress on task categories was successfully updated.'
            this.infMsgDescription = 'Please note all edits are being logged.'
            this.infMsgClass = 'success'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyPoints()
            this.canEditProgressChapters = !this.canEditProgressChapters
          })
          .catch((err) => {
            this.disableAPI = false
            this.infMsgTitle = 'Error. We were not able to update this company because '
            for (let error in err.body.error.exception) {
              this.infMsgTitle += err.body.error.exception[error].message + ' '
            }
            this.infMsgDescription = 'If you believe there is something wrong with our system, please drop us an email to support.team@clearassured.co.uk with more information about what went wrong and we will look into it. Thank you.'
            this.infMsgClass = 'error'
            this.setInformationMsg({
              isShown: true,
              title: this.infMsgTitle,
              text: this.infMsgDescription,
              className: this.infMsgClass
            })
            this.getCompanyPoints()
          })
      },
      createTags (el, wait, blur) {
        const target = this.getTarget(el)
        // const newEmailsSet = {}
        this.emailControl['input'] = target.value
        if (wait && !this.emailControl['isPaste']) {
          this.autoHeight(el)
          return
        }
        if (blur) {
          this.emailControl['isPaste'] = false
          if (this.emailControl['input'] === '') {
            return
          }
        }
        const emails = this.emailControl['input'].split(/[, ]+/)
        for (let index in emails) {
          if (emails[index] !== '') {
            let exists = this.company.approvedEmails.find(item => item.email === emails[index])
            let empty = this.company.approvedEmails.findIndex((item) => item.email === '')
            if (empty !== -1) {
              this.removeElement(empty)
            }
            if (!exists) {
              this.company.approvedEmails.push({
                email: emails[index]
              })
            }
          }
        }
        this.emailControl['isPaste'] = false
        this.emailControl['input'] = ''
        this.addEmailRow()
      },
      autoWidth (el) {
        let target = this.getTarget(el)
        setTimeout(function () {
          if (target) {
            target.style.width = '10px'
            target.style.width = (target.scrollWidth + 15) + 'px'
          }
        }, 0)
      },
      autoHeight (el) {
        let target = this.getTarget(el)
        if (target) {
          setTimeout(function () {
            target.style.height = '40px;'
            target.style.height = (target.scrollHeight + 4) + 'px'
          }, 0)
        }
      },
      getTarget (e) {
        let evn = e || window.event
        return evn.srcElement || e.currentTarget
      },
      goToCompanyList () {
        let props = this.$router.resolve({
          name: 'companies'
        })
        this.$router.push(props.href)
      },
      changeTabActive (tab) {
        this.tabActive = tab
        if (tab === 'profile') {
          this.activeSection = 'companyDetails'
        }
        if (tab === 'progress') {
          this.activeSection = 'companyChart'
        }
      },
      addEmailRow: function () {
        this.company.approvedEmails.push({
          email: ''
        })
      },
      removeElement: function (index) {
        this.company.approvedEmails.splice(index, 1)
      },
      dateSelected (e, model) {
        if (model === 'startDate') {
          this.company.progressDetails.startDate = this.convertDate(e, '/')
          this.datepicker.startShown = false
        } else {
          this.company.details.subscriptionEnd = this.convertDate(e, '/')
          this.datepicker.shown = false
        }
      },
      convertDate (payload, delimiter, monthName) {
        if (!payload) return
        let date = new Date(payload)
        let fullYear = date.getFullYear()
        let month = date.getMonth() + 1
        let day = date.getDate()
        if (monthName) {
          let months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          month = months[month - 1]
        }

        function toDoubleDigit (num) {
          return num.toString().length < 2 ? '0' + num : num.toString()
        }

        return toDoubleDigit(day) + delimiter + toDoubleDigit(month) + delimiter + fullYear
      },
      documentClick (e) {
        let self = this
        if (self.datepicker.shown) {
          let datepicker = document.getElementById('date-selector')
          let target = e.target
          if (!this.isDescendant(datepicker, target)) {
            self.datepicker.shown = false
          }
        }
      },
      isDescendant (parent, child) {
        let node = child.parentNode
        while (node !== null) {
          if (node === parent) {
            return true
          }
          node = node.parentNode
        }
        return false
      },
      changeOrder () {
        this.chapterOrderBy = {
          order: this.chapterOrderBy !== null ? !this.chapterOrderBy.order : true
        }
      },
      getChaptersList () {
        let self = this
        this.$http.get(api.URLS.contentChaptersList, api.headersAuthSettings)
          .then((res) => {
            let chapters = res.body
            let order = ['policies', 'finding', 'assessing', 'retaining']
            chapters.sort(function (x, y) {
              let xkey = order.indexOf(x.slug)
              let ykey = order.indexOf(y.slug)
              return xkey - ykey
            })
            let quarters = this.company.points.sort((a, b) => -1 * (parseInt(a.year + '' + a.quarter) - parseInt(b.year + '' + b.quarter)))
            let lastQuarter = quarters.length ? quarters[0] : null
            let newChapters = []
            let chartData = []
            let quarterData = {}
            let customChapters = {}
            let addThis
            let articleIds = []
            chapters.forEach((chapter) => {
              let articlesForThisChapter = this.articles.filter((article) => article.chapter && article.chapter === chapter.id)
              for (let article in articlesForThisChapter) {
                articlesForThisChapter[article].ttdStatus = 0
                articlesForThisChapter[article].status = null
                articlesForThisChapter[article].loading = false
                articlesForThisChapter[article].resources = []
                articlesForThisChapter[article].done_status = []
                articleIds.push(articlesForThisChapter[article].id)
              }
              let scores = {}
              let score = []
              let i = 1
              customChapters = chapter
              self.reverseItems(quarters).forEach(function (quarter) {
                if (!quarterData[quarter.id]) {
                  quarterData[quarter.id] = []
                }
                if (chapter.slug === 'policies') {
                  let bottom
                  let quarterScore = quarter.policies_score ? quarter.policies_score : 0
                  let benchmarkScore = self.company.details.industry && self.company.details.industry.benchmark_policies_and_procedures ? self.company.details.industry.benchmark_policies_and_procedures : null
                  let countInRange = quarterData[quarter.id].filter(x => x > (quarterScore - 2) && x < (quarterScore + 2)).length
                  let countChapterInRange = quarterData[quarter.id].filter(x => x <= quarterScore - 10).length
                  if (quarterScore <= 5) {
                    addThis = 5
                  }
                  customChapters.top = 100 - (quarterScore + (countChapterInRange * 20)) - addThis
                  quarterData[quarter.id].push(quarterScore)
                  bottom = quarterScore + (countInRange * 0.95)
                  score.push({
                    score: quarterScore,
                    left: i * 140,
                    top: 100 - bottom,
                    benchmark: benchmarkScore
                  })
                } else if (chapter.slug === 'finding') {
                  let bottom
                  let quarterScore = quarter.finding_score ? quarter.finding_score : 0
                  let benchmarkScore = self.company.details.industry && self.company.details.industry.benchmark_finding_talent ? self.company.details.industry.benchmark_finding_talent : null
                  let countInRange = quarterData[quarter.id].filter(x => x > (quarterScore - 2) && x < (quarterScore + 2)).length
                  let countChapterInRange = quarterData[quarter.id].filter(x => x <= quarterScore - 10).length
                  if (quarterScore <= 5) {
                    addThis = 5
                  }
                  customChapters.top = 100 - (quarterScore + (countChapterInRange * 20)) - addThis
                  quarterData[quarter.id].push(quarterScore)
                  bottom = quarterScore + (countInRange * 0.95)
                  score.push({
                    score: quarterScore,
                    left: i * 140,
                    top: 100 - bottom,
                    benchmark: benchmarkScore
                  })
                } else if (chapter.slug === 'assessing') {
                  let bottom
                  let quarterScore = quarter.assessing_score ? quarter.assessing_score : 0
                  let benchmarkScore = self.company.details.industry && self.company.details.industry.benchmark_accessing_talent ? self.company.details.industry.benchmark_accessing_talent : null
                  let countInRange = quarterData[quarter.id].filter(x => x > (quarterScore - 2) && x < (quarterScore + 2)).length
                  let countChapterInRange = quarterData[quarter.id].filter(x => x <= quarterScore - 10).length
                  if (quarterScore <= 5) {
                    addThis = 5
                  }
                  customChapters.top = 100 - (quarterScore + (countChapterInRange * 20)) - addThis
                  quarterData[quarter.id].push(quarterScore)
                  bottom = quarterScore + (countInRange * 0.95)
                  score.push({
                    score: quarterScore,
                    left: i * 140,
                    top: 100 - bottom,
                    benchmark: benchmarkScore
                  })
                } else if (chapter.slug === 'retaining') {
                  let bottom
                  let quarterScore = quarter.retaining_score ? quarter.retaining_score : 0
                  let benchmarkScore = self.company.details.industry && self.company.details.industry.benchmark_retaining_talent ? self.company.details.industry.benchmark_retaining_talent : null
                  let countInRange = quarterData[quarter.id].filter(x => x > (quarterScore - 2) && x < (quarterScore + 2)).length
                  let countChapterInRange = quarterData[quarter.id].filter(x => x <= quarterScore - 10).length
                  if (quarterScore <= 5) {
                    addThis = 5
                  }
                  customChapters.top = 100 - (quarterScore + (countChapterInRange * 20)) - addThis
                  quarterData[quarter.id].push(quarterScore)
                  bottom = quarterScore + (countInRange * 0.95)
                  score.push({
                    score: quarterScore,
                    left: i * 140,
                    top: 100 - bottom,
                    benchmark: benchmarkScore
                  })
                }
                i += 1
              })
              chartData[chapter.id] = {
                chapter: chapter,
                scores: score
              }
              if (chapter.slug === 'policies') {
                let score = lastQuarter ? lastQuarter.policies_score : 0
                scores = {
                  score: score,
                  level: self.getFocusAreaLevel(score)
                }
              } else if (chapter.slug === 'finding') {
                let score = lastQuarter ? lastQuarter.finding_score : 0
                scores = {
                  score: score,
                  level: self.getFocusAreaLevel(score)
                }
              } else if (chapter.slug === 'assessing') {
                let score = lastQuarter ? lastQuarter.assessing_score : 0
                scores = {
                  score: score,
                  level: self.getFocusAreaLevel(score)
                }
              } else if (chapter.slug === 'retaining') {
                let score = lastQuarter ? lastQuarter.retaining_score : 0
                scores = {
                  score: score,
                  level: self.getFocusAreaLevel(score)
                }
              }
              newChapters.push({
                chapter: chapter,
                articles: articlesForThisChapter,
                scores: scores,
                isReady: true,
                keyShown: false
              })
              self.chartData = chartData
            })
            this.chapters = newChapters
            Array.isArray(this.chapters)
            this.getDoneStatuses(articleIds)
            self.$nextTick(() => {
              this.dataLoaded = true
            })
          })
          .catch((err) => {
            console.log(err)
          })
      },
      chapterKeyOpenClose (key) {
        let chapter = this.chapters.find((item) => item.chapter && item.chapter.id === key * 1)
        chapter.keyShown = !chapter.keyShown
      },
      openTTD (chapterId, articleId) {
        let actioned = false
        let chapter = this.chapters.find((item) => item.chapter && item.chapter.id === chapterId)
        for (let article in chapter.articles) {
          if (chapter.articles[article].id === articleId && actioned === false) {
            let newArticle = chapter.articles[article]
            newArticle.open = !newArticle.open
            Vue.set(chapter.articles, article, newArticle)
            actioned = true
            setTimeout(function () {
              let element = document.getElementById('article-messages-' + newArticle.id)
              if (element !== null && newArticle.open) {
                element.scrollTop = element.scrollHeight
              }
            }, 10)
          }
        }
      },
      checkStatus (id) {
        let self = this
        let response = 0
        let article = this.articles.find((article) => article.id === id)
        if (article.done_status !== null && article.done_status.length > 0) {
          let statuses = []
          article.done_status.forEach(status => {
            if (status.company_id === self.companyId * 1) {
              statuses.push(status)
            }
          })
          if (statuses.length) {
            response = statuses[0].status
          }
        }
        return response
      },
      getDoneStatuses (items) {
        let self = this
        items.forEach(function (item) {
          let body = {
            companyId: self.companyId,
            id: item
          }
          self.$http.post(api.URLS.getDoneStatus, body, api.headersAuthSettings)
            .then((res) => {
              let article = self.articles.find((article) => article.id === item * 1)
              if (article.chapter) {
                let chapter = self.chapters.find((chapter) => chapter.chapter.id === article.chapter)
                let index = chapter.articles.findIndex(x => x.id === item * 1)
                if (res.body.length) {
                  let newArticle = article
                  newArticle.done_status = res.body
                  newArticle.ttdStatus = self.checkStatus(article.id)
                  newArticle.loading = false
                  Vue.set(chapter.articles, index, newArticle)
                  setTimeout(function () {
                    let element = document.getElementById('article-messages-' + article.id)
                    if (element !== null) {
                      element.scrollTop = element.scrollHeight
                    }
                  }, 10)
                }
              }
            })
            .catch((err) => console.log(err))
        })
        self.dataLoading = false
      },
      getFocusAreaLevel (score) {
        if (score > 79) {
          return {
            id: 5,
            name: 'Platinum'
          }
        }
        if (score > 59) {
          return {
            id: 4,
            name: 'Gold'
          }
        }
        if (score > 39) {
          return {
            id: 3,
            name: 'Silver'
          }
        }
        if (score > 19) {
          return {
            id: 2,
            name: 'Bronze'
          }
        }
        if (score >= 0) {
          return {
            id: 1,
            name: 'Foundation'
          }
        }
      },
      convertTime (payload, delimiter) {
        if (!payload) return
        let date = new Date(payload)
        let hours = date.getHours()
        let minutes = date.getMinutes()
        let ampm = hours >= 12 ? 'PM' : 'AM'
        let newHours = hours % 12 ? hours % 12 : 12

        function toDoubleDigit (num) {
          return num.toString().length < 2 ? '0' + num : num.toString()
        }

        return toDoubleDigit(newHours) + delimiter + toDoubleDigit(minutes) + ' ' + ampm
      },
      reverseItems (items) {
        if (items) {
          return items.slice().reverse()
        }
      },
      parseJSON (value) {
        return JSON.parse(value)
      },
      getFullServerPath (fileName) {
        return api.serverContenImages + fileName
      },
      getProfileData () {
        let self = this
        this.$http.put(api.URLS.profile, {}, api.headersAuthSettings).then((res) => {
          self.user = res.body
          this.getChaptersList()
        }).catch((err) => {
          console.log(err)
        })
      },
      viewArticle (id) {
        let props = this.$router.resolve({
          name: 'content',
          params: {id: id}
        })
        this.$router.push(props.href)
      },
      getCompanyStatus (company) {
        let today = new Date()
        let endDay = new Date(company.subscription_end)
        if (company.enabled) {
          if (endDay < today) {
            return {
              label: 'Inactive',
              value: 1
            }
          } else {
            return {
              label: 'Active',
              value: 2
            }
          }
        } else {
          return {
            label: 'Archived',
            value: 0
          }
        }
      },
      currentYPosition () {
        if (self.pageYOffset) {
          return self.pageYOffset
        }
        if (document.documentElement && document.documentElement.scrollTop) {
          return document.documentElement.scrollTop
        }
        if (document.body.scrollTop) {
          return document.body.scrollTop
        }
        return 0
      },
      elmYPosition (eID) {
        let elm = document.getElementById(eID)
        let y = elm.offsetTop
        let node = elm
        while (node.offsetParent && node.offsetParent !== document.body) {
          node = node.offsetParent
          y += node.offsetTop
        }
        return y
      },
      smoothScroll (eID) {
        let startY = this.currentYPosition()
        let stopY = this.elmYPosition(eID)
        let distance = stopY > startY ? stopY - startY : startY - stopY
        if (distance < 100) {
          scrollTo(0, stopY)
          return
        }
        let speed = Math.round(distance / 100)
        if (speed >= 20) {
          speed = 20
        }
        let step = Math.round(distance / 25)
        let leapY = stopY > startY ? startY + step : startY - step
        let timer = 0
        if (stopY > startY) {
          for (let i = startY; i < stopY; i += step) {
            setTimeout(function () {
              window.scrollTo(0, leapY)
            }, timer * speed)
            leapY += step
            if (leapY > stopY) {
              leapY = stopY
              timer++
            }
          }
          return
        }
        for (let n = startY; n > stopY; n -= step) {
          setTimeout(function () {
            window.scrollTo(0, leapY)
          }, timer * speed)
          leapY -= step
          if (leapY < stopY) {
            leapY = stopY
            timer++
          }
        }
      },
      getChapterCustomName (chapter) {
        let name = null
        if (chapter.slug === 'finding') {
          if (this.industry && this.industry.chapter_finding_talent) {
            name = this.industry.chapter_finding_talent.title
          } else if (this.industry && this.industry.parent &&
            this.industry.parent.chapter_finding_talent) {
            name = this.industry.parent.chapter_finding_talent.title
          }
        } else if (chapter.slug === 'policies') {
          if (this.industry && this.industry.chapter_policies_and_procedures) {
            name = this.industry.chapter_policies_and_procedures.title
          } else if (this.industry && this.industry.parent &&
            this.industry.parent.chapter_policies_and_procedures) {
            name = this.industry.parent.chapter_policies_and_procedures.title
          }
        } else if (chapter.slug === 'assessing') {
          if (this.industry && this.industry.chapter_accessing_talent) {
            name = this.industry.chapter_accessing_talent.title
          } else if (this.industry && this.industry.parent &&
            this.industry.parent.chapter_accessing_talent) {
            name = this.industry.parent.chapter_accessing_talent.title
          }
        } else if (chapter.slug === 'retaining') {
          if (this.industry && this.industry.chapter_retaining_talent) {
            name = this.industry.chapter_retaining_talent.title
          } else if (this.industry && this.industry.parent &&
            this.industry.parent.chapter_retaining_talent) {
            name = this.industry.parent.chapter_retaining_talent.title
          }
        }

        if (!name) {
          return chapter.name
        } else {
          return name
        }
      },
      goToPageSection (ref) {
        let element = this.$refs[ref]
        if (element) {
          let here = element[0] ? element[0] : element
          here.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
          setTimeout(function () {
            here.focus()
          })
        }
      },
      posY (elm) {
        let test = elm[0] ? elm[0] : elm
        let top = 0
        if (test && test.tagName) {
          while (test !== null && test.tagName.toLowerCase() !== 'body') {
            top += test.offsetTop
            test = test.offsetParent
          }
        }
        return top
      },
      scrollY () {
        if (window.pageYOffset) {
          return window.pageYOffset
        }
        return Math.max(document.documentElement.scrollTop, document.body.scrollTop)
      },
      isScrolledIntoView (el) {
        let elem = el[0] ? el[0] : el
        let docViewTop = this.scrollY()
        let docViewBottom = docViewTop + window.outerHeight
        let elemTop = this.posY(elem) + 300
        let elemBottom = elemTop + elem.offsetHeight
        return ((elemBottom <= docViewBottom) && (elemTop >= docViewTop))
      },
      handleScroll () {
        let sections = this.$refs
        for (let index in sections) {
          if (!index.match(/article/)) {
            let section = sections[index]
            if (section && this.isScrolledIntoView(section)) {
              this.activeSection = index
            }
          }
        }
      },
      // functional for chart
      getCurrentDate () {
        let date = new Date()
        let day = date.getDate()
        let month = date.getMonth() + 1
        let fullYear = date.getFullYear()
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        let m = months[month - 1]
        return m + ' ' + day + ', ' + fullYear
      },
      getBadgeLink () {
        let props = this.$router.resolve({
          name: 'badge'
        })
        return props.href
      },
      getChartWidth (chartData, full) {
        if (chartData !== null) {
          let rightMargin = 0
          if (full) {
            rightMargin = 350
          }
          return (chartData.filter(item => item !== null)[0].scores.length * 140 + rightMargin) + 'px'
        }
      },
      connectPoints () {
        let chapters = document.querySelectorAll('.content-chart-points')
        for (let i = 0; i < chapters.length; i++) {
          let children = chapters[i].querySelectorAll('.content-chart-point')
          for (let n = 0; n < children.length; n++) {
            let isLast = false
            let fromPoint = children[n]
            let toPoint = children[n + 1]
            if (!toPoint) {
              toPoint = document.getElementById(fromPoint.getAttribute('data-chapter'))
              isLast = true
            }
            if (fromPoint !== undefined && toPoint !== undefined) {
              let el1 = fromPoint
              let el2 = toPoint
              let x1
              let x2
              let y1
              let y2
              let color = fromPoint.getAttribute('data-color')

              if (isLast) {
                x1 = el1.offsetLeft + 15
                y1 = (el1.offsetTop + el1.offsetHeight / 2) - 6
                x2 = el2.offsetLeft + 15
                y2 = (el2.offsetTop + el1.offsetHeight / 2) + 5
              } else {
                x1 = el1.offsetLeft + el1.offsetWidth - 2
                y1 = el1.offsetTop + el1.offsetHeight - 11
                x2 = el2.offsetLeft + el2.offsetWidth
                y2 = el2.offsetTop
              }

              let length = Math.sqrt(((x2 - x1) * (x2 - x1)) + ((y2 - y1) * (y2 - y1)))
              let cx = ((x1 + x2) / 2) - (length / 2) - 5
              let cy = ((y1 + y2) / 2) - (3 / 2) + 5
              let angle = Math.atan2((y1 - y2), (x1 - x2)) * (180 / Math.PI)
              let line = document.createElement('div')
              line.style['-webkit-transform'] = 'rotate(' + angle + 'deg)'
              line.style['-moz-transform'] = 'rotate(' + angle + 'deg)'
              line.style['-ms-transform'] = 'rotate(' + angle + 'deg)'
              line.style['-o-transform'] = 'rotate(' + angle + 'deg)'
              line.style['-transform'] = 'rotate(' + angle + 'deg)'
              line.style.width = length + 'px'

              if (isLast) {
                line.style.backgroundColor = 'transparent'
                line.style.borderTop = '3px dashed ' + color
              } else {
                line.style.backgroundColor = color
              }

              line.style.position = 'absolute'
              line.style.left = cx + 'px'
              line.style.top = cy + 'px'
              line.style.height = 3 + 'px'
              line.classList.add('chart-line')
              try {
                document.body.onload = setTimeout(function () {
                  if (document.querySelector('.content-chart-data')) {
                    document.querySelector('.content-chart-data').appendChild(line)
                  }
                }, 500)
              } catch (err) {
              }
              isLast = false
            }
          }
        }
        document.querySelector('.content-chart-body').scrollLeft = document.querySelector('.content-chart-body').scrollWidth
      },
      addChapterHover (chapter, hoverActive) {
        for (let index in this.chartData) {
          if (this.chartData[index].hover && chapter.chapter.id !== this.chartData[index].chapter.id) {
            this.chartData[index].hover.show = false
          }
          if (chapter.chapter.id === this.chartData[index].chapter.id) {
            let editedChapter = this.chartData[index]
            let scores = this.chartData[index].scores
            let top
            let create = this.chartData[index].hover ? !this.chartData[index].hover.show : true
            if (hoverActive === 'benchmark') {
              top = scores[scores.length - 1].benchmark
            } else {
              top = scores[scores.length - 1].score
            }
            editedChapter.hover = {
              type: hoverActive,
              show: create,
              top: top
            }
            Vue.set(this.chartData, index, editedChapter)
          }
        }
      },
      getChapterIndex (c) {
        let scores = []
        let chartData = this.chartData
        let index = 0
        if (c.scores.length) {
          for (let index in chartData) {
            let chapter = chartData[index]
            scores.push(chapter.scores[chapter.scores.length - 1].top)
          }
          let orderedScores = scores.sort((a, b) => a - b)
          index = orderedScores.indexOf(c.scores[c.scores.length - 1].top)
        }
        return index
      },
      hasBenchmark (chapter) {
        if (chapter.scores.length) {
          if (chapter.scores[chapter.scores.length - 1].benchmark !== null) {
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      },
      getIframeCode () {
        let url = location.origin + this.getBadgeLink() + '?company=' + this.company.hash + '&theme=' + this.iframeTheme.value
        return '<iframe width="210" height="85" src="' + url + '" style="border: none;"></iframe>'
      },
      copyIframe () {
        document.getElementById('iframeCode').select()
        document.execCommand('copy')
      },
      reloadIframe () {
        let iframe = document.getElementById('previewIframe')
        iframe.src = iframe.src
      },
      checkIfCompanyCanBeDisconnectedFromBilling () {
        let id = this.companyId
        this.disableAPI = true
        this.$http.get(`${api.URLS.companyCanBeDisconnectedFromBilling}/${id}`, api.headersAuthSettings)
          .then((res) => {
            if (res.body.status === 403 || res.body.status === 409) {
              this.confOpened = 'disconnectFromBilling'
              if (res.body.status === 403) {
                this.initConfAction({
                  actionName: 'disconnectFromBillingError',
                  customText: res.body.message
                })
              } else {
                this.initConfAction({
                  actionName: 'disconnectFromBilling'
                })
              }
            } else {
              this.disconnectedFromBillingCheckConfirmed = true
              this.company.details.disconnectedFromBilling = true
              this.$nextTick(() => {
                this.disconnectedFromBillingCheckConfirmed = false
              })
            }
            this.disableAPI = false
          })
          .catch(() => {
            this.disableAPI = false
          })
        this.getConfData.actionConfirmed = false
      },
      checkIfCompanyCanBeDisconnectedFromBillingConfirmed () {
        this.disconnectedFromBillingCheckConfirmed = true
        this.company.details.disconnectedFromBilling = true
        this.$nextTick(() => {
          this.disconnectedFromBillingCheckConfirmed = false
        })
      }
    },
    components: {
      BillingProfile,
      AppSelect
    },
    watch: {
      filteredChapters: function () {
        let editedChapters = []
        this.filteredChapters.forEach((chapter) => {
          editedChapters.push({
            quarter: chapter.quarter,
            year: chapter.year,
            score: {
              policies: chapter.policies,
              finding: chapter.finding,
              assessing: chapter.assessing,
              retaining: chapter.retaining
            }
          })
        })
        this.editedChapters = editedChapters
      },
      'getConfData.actionConfirmed' (value) {
        if (value) {
          if (this.confOpened === 'delete') this.deleteCompanyConfirmed()
          if (this.confOpened === 'reset') this.resetTaskHistoryConfirmed()
          if (this.confOpened === 'archive') this.archiveCompanyConfirmed()
          if (this.confOpened === 'changeUrl') this.changeUrlConfirmed()
          if (this.confOpened === 'disconnectFromBilling') this.checkIfCompanyCanBeDisconnectedFromBillingConfirmed()
          if (this.confOpened === 'deleteStatusFile' || this.confOpened === 'deleteStatusMessage' || this.confOpened === 'deleteStatusFileMessage') {
            this.deleteStatusConfirmed()
          }
          this.clearConfAction()
        }
      },
      // 'company.details.url' (value) {
      //   if (value && value !== this.initialData.details.url && this.changeUrl === false) {
      //     this.confOpened = 'changeUrl'
      //     this.initConfAction({
      //       actionName: 'changeCompanyUrl'
      //     })
      //   }
      // },
      'company.details.allowAllEmails' (value) {
        this.company.details.emailApproval = this.yesNoOptionsForSelectNew.find((item) => item.value === this.company.details.emailApproval.value)
      },
      'company.details.disconnectedFromBilling' (newVal, oldVal) {
        if (newVal !== oldVal && newVal === true) {
          if (!this.disconnectedFromBillingCheckConfirmed) {
            this.checkIfCompanyCanBeDisconnectedFromBilling()
            this.$nextTick(() => {
              this.company.details.disconnectedFromBilling = false
            })
          }
        }
      },
      tabActive: {
        handler: function (value) {
          if (value === 'progress') {
            let self = this
            setTimeout(function () {
              self.connectPoints()
            }, 1000)
            document.title = this.company.details.name + '\'s progress | Clear Assured'
            if (window._paq) {
              window._paq.push(['setCustomUrl', location.pathname])
              window._paq.push(['setDocumentTitle', document.title])
              window._paq.push(['trackPageView'])
            }
            // if (window.ga) {
            //   window.ga('set', 'contentGroup1', 'ADMINISTRATION_AREA')
            //   window.ga('send', 'pageview', {
            //     'page': location.pathname,
            //     'location': location.href,
            //     'title': document.title
            //   })
            // }
          }
          if (value === 'profile') {
            document.title = this.company.details.name + '\'s details | Clear Assured'
            if (window._paq) {
              window._paq.push(['setCustomUrl', location.pathname])
              window._paq.push(['setDocumentTitle', document.title])
              window._paq.push(['trackPageView'])
            }
            // if (window.ga) {
            //   window.ga('set', 'contentGroup1', 'ADMINISTRATION_AREA')
            //   window.ga('send', 'pageview', {
            //     'page': location.pathname,
            //     'location': location.href,
            //     'title': document.title
            //   })
            // }
          }
        },
        deep: true
      },
      '$route' (to, from) {
        if (to.params.id !== from.params.id) {
          location.reload()
        }
      }
    },
    beforeMount () {
      this.getChapters()
      this.getTaskAssignee(this.companyId)
      this.getAdminUsers()
      // this.getCompanies()
      // this.getIndustries()
    },
    created () {
      this.companyId = parseInt(this.$route.params.id)
      document.onclick = this.documentClick
      window.addEventListener('scroll', this.handleScroll)
    },
    mounted () {
      this.getCompanyData()
      this.getCompanyPoints()
      this.getCompanies()
      this.getIndustries()
      this.getProfileData()
      this.getCompanySizes()
      this.getCompanyLocations()
      this.getDefaultRegistrationRoles()
    },
    destroyed () {
      window.removeEventListener('scroll', this.handleScroll)
    }
  }
</script>

<style src="@/assets/scss/components/dashboard.scss" lang="scss" scoped/>
<style src='@/assets/scss/components/profile.scss' lang='scss'/>
<style src='@/assets/scss/components/todo-list.scss' lang='scss' scoped/>
<style>
  .company-edit .edit-details, .company-edit .edit-password {
    top: 38px !important;
  }
</style>



// WEBPACK FOOTER //
// EditCompany.vue?227fbf3f